import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaPlus, FaSearch, FaEdit, FaTrash, FaTimes, FaFileImport } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import './Suppliers.css';

const API = import.meta.env.VITE_API_URL;
const PAGE_SIZE = 50;

const emptySupplier = {
  supplier_code: '',
  name: '',
  contact_name: '',
  contact_title: '',
  email: '',
  phone: '',
  mobile: '',
  website: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  postal_code: '',
  country: '',
  payment_terms: '',
  lead_time_days: '',
  default_currency: 'USD',
  min_order_amount: '',
  tax_id: '',
  preferred_shipping_method: '',
  notes: '',
  active: true,
};

const supplierFieldDefinitions = [
  {
    key: 'supplier_code',
    label: 'Supplier Code',
    description: 'Unique identifier used for lookups',
    matches: ['suppliercode', 'vendorcode', 'code'],
  },
  {
    key: 'name',
    label: 'Supplier Name',
    description: 'Display name (required)',
    matches: ['name', 'suppliername', 'vendor', 'vendorname'],
    required: true,
  },
  {
    key: 'contact_name',
    label: 'Contact Name',
    description: 'Primary contact full name',
    matches: ['contact', 'contactname', 'buyer'],
  },
  {
    key: 'contact_title',
    label: 'Contact Title',
    matches: ['title', 'contacttitle', 'role'],
  },
  {
    key: 'email',
    label: 'Email',
    matches: ['email', 'emailaddress'],
  },
  {
    key: 'phone',
    label: 'Phone',
    matches: ['phone', 'phone1', 'telephone'],
  },
  {
    key: 'mobile',
    label: 'Mobile',
    matches: ['mobile', 'cell', 'phone2'],
  },
  {
    key: 'website',
    label: 'Website',
    matches: ['website', 'url'],
  },
  {
    key: 'address1',
    label: 'Address Line 1',
    matches: ['address', 'address1', 'street'],
  },
  {
    key: 'address2',
    label: 'Address Line 2',
    matches: ['address2', 'suite', 'apt'],
  },
  {
    key: 'city',
    label: 'City',
    matches: ['city', 'town'],
  },
  {
    key: 'state',
    label: 'State / Province',
    matches: ['state', 'province', 'region'],
  },
  {
    key: 'postal_code',
    label: 'Postal Code',
    matches: ['zip', 'postal', 'postalcode'],
  },
  {
    key: 'country',
    label: 'Country',
    matches: ['country'],
  },
  {
    key: 'payment_terms',
    label: 'Payment Terms',
    matches: ['paymentterms', 'terms'],
  },
  {
    key: 'lead_time_days',
    label: 'Lead Time (days)',
    description: 'Number of days to fulfill orders',
    matches: ['leadtime', 'leadtime_days'],
    type: 'integer',
  },
  {
    key: 'default_currency',
    label: 'Default Currency',
    matches: ['currency', 'defaultcurrency'],
    type: 'upper',
  },
  {
    key: 'min_order_amount',
    label: 'Min Order Amount',
    matches: ['minorder', 'minorderamount', 'moq'],
    type: 'number',
  },
  {
    key: 'tax_id',
    label: 'Tax ID / VAT',
    matches: ['taxid', 'vat'],
  },
  {
    key: 'preferred_shipping_method',
    label: 'Preferred Shipping Method',
    matches: ['shipping', 'shipmethod'],
  },
  {
    key: 'notes',
    label: 'Notes',
    matches: ['notes', 'memo', 'comments'],
  },
  {
    key: 'active',
    label: 'Active',
    description: 'Indicates if the supplier should be enabled',
    matches: ['active', 'status', 'enabled'],
    type: 'boolean',
  },
];

const MAX_IMPORT_ROWS = 500;
const requiredFieldKeys = supplierFieldDefinitions
  .filter((field) => field.required)
  .map((field) => field.key);
const supplierFieldKeys = supplierFieldDefinitions.map((field) => field.key);
const supplierFieldByKey = supplierFieldDefinitions.reduce((acc, field) => {
  acc[field.key] = field;
  return acc;
}, {});

const hasMeaningfulValue = (value) => !(value === '' || value === null || value === undefined);

const fieldLabelFor = (fieldKey) => supplierFieldByKey[fieldKey]?.label || fieldKey;

const extractIdFromLocationHeader = (header) => {
  if (!header) return null;
  try {
    const sanitized = String(header).split('?')[0].trim();
    const match = sanitized.match(/\/(\d+)(?:\/?$)/);
    if (!match) return null;
    const id = Number(match[1]);
    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
};

const normalizeColumnIndex = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const num = Number(value);
  return Number.isFinite(num) && num >= 0 ? num : null;
};

const normalizeMatchKey = (value = '') => (
  value ? value.toString().trim().toLowerCase().replace(/[^a-z0-9]/g, '') : ''
);

const guessInitialMapping = (headers = []) => {
  const normalizedHeaders = headers.map((header, idx) => ({
    idx,
    match: normalizeMatchKey(header || `col_${idx}`),
  }));
  const usedIndices = new Set();
  const mapping = {};
  supplierFieldDefinitions.forEach((field) => {
    const matchTargets = new Set([
      normalizeMatchKey(field.key),
      normalizeMatchKey(field.label),
      ...(field.matches || []).map(normalizeMatchKey),
    ]);
    const column = normalizedHeaders.find((col) => !usedIndices.has(col.idx) && matchTargets.has(col.match));
    if (column) {
      mapping[field.key] = column.idx;
      usedIndices.add(column.idx);
    }
  });
  return mapping;
};

const cleanNumericValue = (value, { integer = false } = {}) => {
  if (value === '' || value === null || value === undefined) return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return integer ? Math.round(num) : num;
};

const prepareSupplierPayloadForSave = (data) => ({
  ...data,
  lead_time_days: cleanNumericValue(data.lead_time_days, { integer: true }),
  min_order_amount: cleanNumericValue(data.min_order_amount),
});

const parseBooleanFromValue = (value) => {
  if (value === true || value === false) return value;
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim().toLowerCase();
  if (!normalized) return null;
  if (['1', 'true', 'yes', 'y', 'active', 'enabled'].includes(normalized)) return true;
  if (['0', 'false', 'no', 'n', 'inactive', 'disabled'].includes(normalized)) return false;
  return null;
};

const convertImportedValue = (field, raw) => {
  if (raw === null || raw === undefined) return '';
  let value = raw;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    value = trimmed;
  }
  switch (field.type) {
    case 'boolean': {
      const boolVal = parseBooleanFromValue(value);
      if (boolVal === null) return value;
      return boolVal;
    }
    case 'integer':
    case 'number': {
      const num = Number(value);
      if (Number.isFinite(num)) return num;
      return String(value);
    }
    case 'upper':
      return String(value).toUpperCase();
    default:
      return typeof value === 'string' ? value : String(value);
  }
};

const buildPayloadFromRow = (rowValues = [], mappingEntries = []) => {
  const payload = { ...emptySupplier };
  mappingEntries.forEach(({ fieldKey, columnIndex }) => {
    const fieldDef = supplierFieldByKey[fieldKey];
    if (!fieldDef) return;
    const rawValue = Array.isArray(rowValues) ? rowValues[columnIndex] : undefined;
    if (!hasMeaningfulValue(rawValue)) return;
    const converted = convertImportedValue(fieldDef, rawValue);
    if (!hasMeaningfulValue(converted)) return;
    payload[fieldKey] = converted;
  });
  return prepareSupplierPayloadForSave(payload);
};

const coerceValueForComparison = (field, value) => {
  if (value === undefined || value === null) return null;
  if (field?.type === 'integer') {
    const num = Number(value);
    return Number.isFinite(num) ? Math.round(num) : null;
  }
  if (field?.type === 'number') {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }
  if (field?.type === 'boolean') {
    const boolVal = parseBooleanFromValue(value);
    return typeof boolVal === 'boolean' ? boolVal : null;
  }
  if (field?.type === 'upper') {
    return String(value).toUpperCase();
  }
  return String(value).trim();
};

const valuesMatch = (field, expected, actual) => {
  const normalizedExpected = coerceValueForComparison(field, expected);
  const normalizedActual = coerceValueForComparison(field, actual);
  if (normalizedExpected === null || normalizedExpected === '') {
    return normalizedActual === null || normalizedActual === '';
  }
  return normalizedExpected === normalizedActual;
};

const currencyOptions = ['USD', 'EUR', 'CAD', 'GBP', 'MXN', 'CNY', 'JPY'];

function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [meta, setMeta] = useState({ total: 0, limit: PAGE_SIZE, offset: 0 });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptySupplier);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState(() => new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [importPanelOpen, setImportPanelOpen] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [importHeaders, setImportHeaders] = useState([]);
  const [importRows, setImportRows] = useState([]);
  const [importMapping, setImportMapping] = useState({});
  const [importNotice, setImportNotice] = useState('');
  const [importError, setImportError] = useState('');
  const [importSummary, setImportSummary] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ processed: 0, total: 0 });
  const [draggingColumn, setDraggingColumn] = useState(null);
  const [dragOverField, setDragOverField] = useState(null);
  const fileInputRef = useRef(null);
  const selectAllCheckboxRef = useRef(null);

  const normalizedMappingEntries = useMemo(
    () => Object.entries(importMapping)
      .map(([fieldKey, columnIndex]) => ({
        fieldKey,
        columnIndex: normalizeColumnIndex(columnIndex),
      }))
      .filter(({ fieldKey, columnIndex }) => supplierFieldByKey[fieldKey] && Number.isInteger(columnIndex)),
    [importMapping]
  );

  const normalizedMappingByField = useMemo(() => {
    const map = {};
    normalizedMappingEntries.forEach(({ fieldKey, columnIndex }) => {
      map[fieldKey] = columnIndex;
    });
    return map;
  }, [normalizedMappingEntries]);

  const mappedColumns = useMemo(
    () => new Set(normalizedMappingEntries.map(({ columnIndex }) => columnIndex)),
    [normalizedMappingEntries]
  );

  const readyRowCount = useMemo(() => {
    if (!importRows.length || !normalizedMappingEntries.length) return 0;
    return importRows.reduce((count, row) => {
      const payload = buildPayloadFromRow(row, normalizedMappingEntries);
      if (payload.name && String(payload.name).trim()) return count + 1;
      return count;
    }, 0);
  }, [importRows, normalizedMappingEntries]);

  const previewPayloads = useMemo(() => {
    if (!importRows.length || !normalizedMappingEntries.length) return [];
    return importRows
      .map((row) => buildPayloadFromRow(row, normalizedMappingEntries))
      .filter((payload) => payload.name && String(payload.name).trim())
      .slice(0, 5);
  }, [importRows, normalizedMappingEntries]);

  const requiredMappingsSatisfied = useMemo(
    () => requiredFieldKeys.every((key) => Number.isInteger(normalizedMappingByField[key])),
    [normalizedMappingByField]
  );

  const missingRequiredFields = useMemo(
    () => requiredFieldKeys.filter((key) => !Number.isInteger(normalizedMappingByField[key])),
    [normalizedMappingByField]
  );
  const currentPageIds = useMemo(() => suppliers.map((s) => s.id), [suppliers]);
  const selectedCount = selectedSupplierIds.size;
  const hasSelectedSuppliers = selectedCount > 0;
  const allVisibleSelected = useMemo(
    () => currentPageIds.length > 0 && currentPageIds.every((id) => selectedSupplierIds.has(id)),
    [currentPageIds, selectedSupplierIds]
  );
  const someVisibleSelected = useMemo(
    () => currentPageIds.some((id) => selectedSupplierIds.has(id)),
    [currentPageIds, selectedSupplierIds]
  );

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = !allVisibleSelected && someVisibleSelected;
    }
  }, [allVisibleSelected, someVisibleSelected]);

  const friendlyColumnLabel = (idx) => {
    const label = importHeaders[idx];
    if (label === null || label === undefined) return `Column ${idx + 1}`;
    const trimmed = String(label).trim();
    return trimmed || `Column ${idx + 1}`;
  };

  const resetImportState = () => {
    setImportFileName('');
    setImportHeaders([]);
    setImportRows([]);
    setImportMapping({});
    setImportNotice('');
    setImportError('');
    setImportSummary(null);
    setImportProgress({ processed: 0, total: 0 });
    setDragOverField(null);
    setDraggingColumn(null);
  };

  const toggleSelectSupplier = (supplierId) => {
    setSelectedSupplierIds((prev) => {
      const next = new Set(prev);
      if (next.has(supplierId)) next.delete(supplierId);
      else next.add(supplierId);
      return next;
    });
  };

  const handleToggleSelectAllOnPage = () => {
    if (!suppliers.length) return;
    setSelectedSupplierIds((prev) => {
      const next = new Set(prev);
      const everySelected = suppliers.every((supplier) => next.has(supplier.id));
      if (everySelected) {
        suppliers.forEach((supplier) => next.delete(supplier.id));
      } else {
        suppliers.forEach((supplier) => next.add(supplier.id));
      }
      return next;
    });
  };

  const clearSelectedSuppliers = () => setSelectedSupplierIds(new Set());

  const openBulkDeleteModal = () => {
    if (!hasSelectedSuppliers) return;
    setBulkDeleteOpen(true);
  };

  const closeBulkDeleteModal = () => {
    if (bulkDeleting) return;
    setBulkDeleteOpen(false);
  };

  const runBulkDelete = async () => {
    if (!hasSelectedSuppliers) return;
    setBulkDeleting(true);
    const idsToDelete = Array.from(selectedSupplierIds);
    const failed = [];
    try {
      for (const supplierId of idsToDelete) {
        try {
          const res = await fetch(`${API}/api/suppliers/${supplierId}`, { method: 'DELETE' });
          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body?.error || `Request failed (${res.status})`);
          }
        } catch (err) {
          failed.push({
            supplierId,
            message: err.message || 'Failed to delete supplier',
          });
        }
      }
      if (idsToDelete.length > failed.length) {
        clearSelectedSuppliers();
        fetchSuppliers(meta?.offset || 0);
      }
      if (failed.length) {
        const sample = failed
          .slice(0, 3)
          .map((entry) => `ID ${entry.supplierId}: ${entry.message}`)
          .join('\n');
        alert(`Unable to delete ${failed.length} supplier${failed.length === 1 ? '' : 's'}.\n${sample}`);
        setSelectedSupplierIds(new Set(failed.map((entry) => entry.supplierId)));
      } else {
        setBulkDeleteOpen(false);
      }
    } finally {
      setBulkDeleting(false);
    }
  };

  const assignColumnToField = (fieldKey, columnIndex) => {
    setImportMapping((prev) => {
      const next = { ...prev };
      const normalizedIndex = normalizeColumnIndex(columnIndex);
      Object.keys(next).forEach((key) => {
        if (key === fieldKey) return;
        const existingIndex = normalizeColumnIndex(next[key]);
        if (existingIndex !== null && normalizedIndex !== null && existingIndex === normalizedIndex) {
          delete next[key];
        }
      });
      if (normalizedIndex === null) {
        delete next[fieldKey];
      } else {
        next[fieldKey] = normalizedIndex;
      }
      return next;
    });
  };

  const handleColumnDragStart = (columnIndex, event) => {
    if (!Number.isFinite(columnIndex)) return;
    setDraggingColumn(columnIndex);
    if (event?.dataTransfer) {
      event.dataTransfer.setData('text/column-index', String(columnIndex));
      event.dataTransfer.setData('text/plain', String(columnIndex));
      event.dataTransfer.effectAllowed = 'move';
    }
  };

  const handleColumnDragEnd = () => setDraggingColumn(null);

  const handleFieldDragOver = (fieldKey, event) => {
    event.preventDefault();
    if (dragOverField !== fieldKey) setDragOverField(fieldKey);
    if (event?.dataTransfer) event.dataTransfer.dropEffect = 'move';
  };

  const handleFieldDragLeave = (fieldKey) => {
    if (dragOverField === fieldKey) setDragOverField(null);
  };

  const handleFieldDrop = (fieldKey, event) => {
    event.preventDefault();
    const textValue = event.dataTransfer.getData('text/column-index') || event.dataTransfer.getData('text/plain');
    const columnIndex = Number(textValue);
    if (!Number.isNaN(columnIndex)) assignColumnToField(fieldKey, columnIndex);
    setDragOverField(null);
  };

  const fetchSupplierRecord = async (supplierId) => {
    if (!Number.isFinite(supplierId) || supplierId <= 0) return null;
    try {
      const res = await fetch(`${API}/api/suppliers/${supplierId}`);
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  };

  const parseImportFile = async (file) => {
    setImportError('');
    setImportNotice('');
    setImportSummary(null);
    setImportProgress({ processed: 0, total: 0 });
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames?.[0];
      if (!firstSheetName) throw new Error('No worksheet found in file.');
      const worksheet = workbook.Sheets[firstSheetName];
      const rawRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, blankrows: false });
      if (!rawRows.length) throw new Error('No rows detected in the uploaded file.');
      const headerRow = (rawRows[0] || []).map((cell, idx) => {
        if (cell === null || cell === undefined) return `Column ${idx + 1}`;
        const trimmed = String(cell).trim();
        return trimmed || `Column ${idx + 1}`;
      });
      const bodyRows = rawRows.slice(1).map((row) => (Array.isArray(row) ? row : []));
      const filteredRows = bodyRows.filter((row) => row.some((cell) => {
        if (cell === null || cell === undefined) return false;
        return String(cell).trim() !== '';
      }));
      if (!filteredRows.length) throw new Error('No supplier rows found under the header.');
      const limitedRows = filteredRows.slice(0, MAX_IMPORT_ROWS);
      setImportHeaders(headerRow);
      setImportRows(limitedRows);
      setImportMapping(guessInitialMapping(headerRow));
      setImportFileName(file.name);
      if (filteredRows.length > MAX_IMPORT_ROWS) {
        setImportNotice(`Loaded first ${MAX_IMPORT_ROWS} rows (of ${filteredRows.length}).`);
      } else {
        setImportNotice(`Loaded ${limitedRows.length} row${limitedRows.length === 1 ? '' : 's'} from ${file.name}.`);
      }
    } catch (err) {
      console.error('Supplier import parse error', err);
      setImportError(err.message || 'Failed to read the uploaded file.');
    }
  };

  const handleImportFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (file) await parseImportFile(file);
    if (event?.target) event.target.value = '';
  };

  const handlePickFile = () => fileInputRef.current?.click();

  const handleResetImport = () => {
    if (importing) return;
    resetImportState();
  };

  const handleRunImport = async () => {
    if (!importRows.length) {
      setImportError('Upload a CSV file to begin.');
      return;
    }
    if (!requiredMappingsSatisfied) {
      const missingLabels = missingRequiredFields.map(fieldLabelFor).join(', ');
      setImportError(`Please map required fields: ${missingLabels}.`);
      return;
    }
    const rowsWithPayload = importRows
      .map((row, idx) => ({ idx, payload: buildPayloadFromRow(row, normalizedMappingEntries) }))
      .filter(({ payload }) => payload.name && String(payload.name).trim());

    if (!rowsWithPayload.length) {
      setImportError('No valid supplier rows were detected after mapping.');
      return;
    }

    setImporting(true);
    setImportError('');
    setImportSummary(null);
    setImportProgress({ processed: 0, total: rowsWithPayload.length });

    const errors = [];
    const mismatchRows = [];
    const patchFailures = [];
    let importedCount = 0;

    try {
      for (let i = 0; i < rowsWithPayload.length; i += 1) {
        const { idx, payload } = rowsWithPayload[i];
        try {
          const res = await fetch(`${API}/api/suppliers`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          let responseBody = null;
          try {
            responseBody = await res.json();
          } catch (jsonErr) {
            responseBody = null;
          }
          if (!res.ok) {
            throw new Error(responseBody?.error || `Request failed (${res.status})`);
          }
          importedCount += 1;

          const locationHeader = res.headers?.get
            ? (res.headers.get('location') || res.headers.get('Location'))
            : '';
          let supplierRecord = responseBody && typeof responseBody === 'object' ? responseBody : null;
          let supplierId = Number(supplierRecord?.id);
          if (!Number.isFinite(supplierId)) supplierId = extractIdFromLocationHeader(locationHeader);
          if ((!supplierRecord || typeof supplierRecord !== 'object') && Number.isFinite(supplierId)) {
            supplierRecord = await fetchSupplierRecord(supplierId);
          }
          const effectiveRecord = supplierRecord && typeof supplierRecord === 'object' ? supplierRecord : null;
          const mismatchedFields = supplierFieldKeys.filter((fieldKey) => {
            const expectedValue = payload[fieldKey];
            if (!hasMeaningfulValue(expectedValue)) return false;
            if (!effectiveRecord) return true;
            const fieldDef = supplierFieldByKey[fieldKey];
            const actualValue = effectiveRecord[fieldKey];
            return !valuesMatch(fieldDef, expectedValue, actualValue);
          });

          if (mismatchedFields.length) {
            mismatchRows.push({
              rowNumber: idx + 2,
              supplierName: payload.name || `Row ${idx + 2}`,
              missingFields: mismatchedFields.map(fieldLabelFor),
            });
            if (Number.isFinite(supplierId)) {
              try {
                const patchRes = await fetch(`${API}/api/suppliers/${supplierId}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(payload),
                });
                if (!patchRes.ok) {
                  const patchBody = await patchRes.json().catch(() => ({}));
                  throw new Error(patchBody?.error || `Request failed (${patchRes.status})`);
                }
              } catch (patchErr) {
                patchFailures.push({
                  rowNumber: idx + 2,
                  supplierName: payload.name || `Row ${idx + 2}`,
                  message: patchErr.message || 'Failed to sync mapped fields',
                });
              }
            } else {
              patchFailures.push({
                rowNumber: idx + 2,
                supplierName: payload.name || `Row ${idx + 2}`,
                message: 'Unable to determine supplier id to apply mapped fields',
              });
            }
          }
        } catch (err) {
          errors.push({
            row: idx + 2,
            message: err.message || 'Failed to import row',
          });
        }
        setImportProgress({ processed: i + 1, total: rowsWithPayload.length });
      }
      setImportSummary({
        total: rowsWithPayload.length,
        imported: importedCount,
        failed: errors.length,
        errors: errors.slice(0, 5),
        hasMoreErrors: errors.length > 5,
        mismatches: mismatchRows.slice(0, 5),
        mismatchesCount: mismatchRows.length,
        patchFailures: patchFailures.slice(0, 5),
        patchFailuresCount: patchFailures.length,
      });
      fetchSuppliers(meta?.offset || 0);
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Suppliers', subtitle: 'Vendor Directory' }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, []);

  const fetchSuppliers = async (offset = 0) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(offset),
        sortField: 'name',
        sortOrder: 'ASC',
      });
      if (search.trim()) params.set('q', search.trim());
      if (!showInactive) params.set('active', 'active');
      const res = await fetch(`${API}/api/suppliers?${params.toString()}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Request failed (${res.status})`);
      }
      const json = await res.json();
      setSuppliers(Array.isArray(json?.data) ? json.data : []);
      setMeta(json?.meta || { total: 0, limit: PAGE_SIZE, offset });
    } catch (err) {
      console.error(err);
      setError(err.message || 'Unable to load suppliers');
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, showInactive]);

  const totalPages = useMemo(() => {
    if (!meta?.limit) return 1;
    return Math.max(1, Math.ceil((meta?.total || 0) / meta.limit));
  }, [meta]);

  const currentPage = useMemo(() => {
    if (!meta?.limit) return 1;
    return Math.floor((meta?.offset || 0) / meta.limit) + 1;
  }, [meta]);

  const changePage = (direction) => {
    const nextPage = currentPage + direction;
    if (nextPage < 1 || nextPage > totalPages) return;
    const newOffset = (nextPage - 1) * (meta?.limit || PAGE_SIZE);
    fetchSuppliers(newOffset);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptySupplier });
    setFormError('');
    setFormOpen(true);
    setSelectedSupplierId(null);
  };

  const openEdit = (supplier) => {
    setEditing(supplier);
    setSelectedSupplierId(supplier?.id || null);
    setForm({
      supplier_code: supplier.supplier_code || '',
      name: supplier.name || '',
      contact_name: supplier.contact_name || '',
      contact_title: supplier.contact_title || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      mobile: supplier.mobile || '',
      website: supplier.website || '',
      address1: supplier.address1 || '',
      address2: supplier.address2 || '',
      city: supplier.city || '',
      state: supplier.state || '',
      postal_code: supplier.postal_code || '',
      country: supplier.country || '',
      payment_terms: supplier.payment_terms || '',
      lead_time_days: supplier.lead_time_days ?? '',
      default_currency: supplier.default_currency || 'USD',
      min_order_amount: supplier.min_order_amount ?? '',
      tax_id: supplier.tax_id || '',
      preferred_shipping_method: supplier.preferred_shipping_method || '',
      notes: supplier.notes || '',
      active: supplier.active ?? true,
    });
    setFormError('');
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setFormError('');
    setForm({ ...emptySupplier });
    setEditing(null);
    setSelectedSupplierId(null);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setFormError('');
    try {
      if (!form.name.trim()) {
        setFormError('Supplier name is required.');
        setSaving(false);
        return;
      }
      const payload = {
        ...form,
        lead_time_days: form.lead_time_days === '' ? null : Number(form.lead_time_days),
        min_order_amount: form.min_order_amount === '' ? null : Number(form.min_order_amount),
      };
      const url = editing ? `${API}/api/suppliers/${editing.id}` : `${API}/api/suppliers`;
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Request failed (${res.status})`);
      }
      closeForm();
      fetchSuppliers(meta?.offset || 0);
    } catch (err) {
      console.error(err);
      setFormError(err.message || 'Failed to save supplier');
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = (supplier) => {
    setDeleteTarget(supplier);
  };

  const cancelDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const targetId = deleteTarget.id;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/api/suppliers/${deleteTarget.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Request failed (${res.status})`);
      }
      setDeleteTarget(null);
      setSelectedSupplierIds((prev) => {
        if (!prev.has(targetId)) return prev;
        const next = new Set(prev);
        next.delete(targetId);
        return next;
      });
      fetchSuppliers(meta?.offset || 0);
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to delete supplier');
    } finally {
      setDeleting(false);
    }
  };

  const clearSearch = () => setSearch('');

  return (
    <div className="suppliers-wrapper">
      <div className="suppliers-toolbar">
        <div className="search-field">
          <FaSearch />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, code, contact…"
          />
          {search && (
            <button className="icon-btn" onClick={clearSearch} aria-label="Clear search">
              <FaTimes />
            </button>
          )}
        </div>
        <label className="toggle-field">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show inactive
        </label>
        <button className="btn" onClick={() => setImportPanelOpen((prev) => !prev)}>
          <FaFileImport /> {importPanelOpen ? 'Hide Import' : 'Import CSV'}
        </button>
        <button
          className="btn danger"
          onClick={openBulkDeleteModal}
          disabled={!hasSelectedSuppliers || bulkDeleting}
          title="Delete selected suppliers"
        >
          <FaTrash /> Delete
        </button>
        <button className="btn primary" onClick={openCreate}>
          <FaPlus /> New Supplier
        </button>
      </div>

      {importPanelOpen && (
        <div className="suppliers-import-panel">
          <div className="import-panel-header">
            <div>
              <h3>Smart Supplier Import</h3>
              <p>Upload a CSV/Excel file and drag columns to the supplier fields.</p>
            </div>
            <div className="import-panel-actions">
              <button className="btn" type="button" onClick={handleResetImport} disabled={importing}>Reset</button>
              <button
                className="icon-btn"
                type="button"
                onClick={() => !importing && setImportPanelOpen(false)}
                disabled={importing}
                aria-label="Close import panel"
              >
                <FaTimes />
              </button>
            </div>
          </div>
          <div className="import-file-row">
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleImportFileChange}
              accept=".csv,.tsv,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            />
            <button className="btn" type="button" onClick={handlePickFile} disabled={importing}>
              <FaFileImport /> {importFileName ? 'Replace File' : 'Choose File'}
            </button>
            <span className="import-file-name">{importFileName || 'No file selected yet'}</span>
            {importRows.length > 0 && (
              <span className="import-file-meta">
                {importRows.length} row{importRows.length === 1 ? '' : 's'} parsed (max {MAX_IMPORT_ROWS})
              </span>
            )}
          </div>
          {importNotice && <div className="suppliers-note">{importNotice}</div>}
          {importError && <div className="suppliers-error">{importError}</div>}
          {importSummary && (
            <div className={`suppliers-note ${importSummary.failed ? 'warning' : 'success'}`}>
              Imported {importSummary.imported} of {importSummary.total} row{importSummary.total === 1 ? '' : 's'}.
              {importSummary.failed ? ` ${importSummary.failed} failed.` : ' All rows succeeded.'}
              {!!importSummary.errors?.length && (
                <ul className="import-error-list">
                  {importSummary.errors.map((err, idx) => (
                    <li key={`import-error-${idx}`}>Row {err.row}: {err.message}</li>
                  ))}
                  {importSummary.hasMoreErrors && <li>Additional errors truncated...</li>}
                </ul>
              )}
            </div>
          )}
          {importSummary?.mismatchesCount > 0 && (
            <div className="suppliers-note warning">
              Synced mapped fields for {importSummary.mismatchesCount} row{importSummary.mismatchesCount === 1 ? '' : 's'} due to partial API responses.
              {!!importSummary.mismatches?.length && (
                <ul className="import-error-list">
                  {importSummary.mismatches.map((row, idx) => (
                    <li key={`import-mismatch-${idx}`}>
                      Row {row.rowNumber} ({row.supplierName}): {row.missingFields.join(', ')}
                    </li>
                  ))}
                  {importSummary.mismatchesCount > importSummary.mismatches.length && <li>Additional sync notices truncated...</li>}
                </ul>
              )}
            </div>
          )}
          {importSummary?.patchFailuresCount > 0 && (
            <div className="suppliers-error">
              Unable to apply mapped fields for {importSummary.patchFailuresCount} row{importSummary.patchFailuresCount === 1 ? '' : 's'}. Please review those suppliers manually.
              <ul className="import-error-list">
                {importSummary.patchFailures.map((row, idx) => (
                  <li key={`import-patchfail-${idx}`}>
                    Row {row.rowNumber} ({row.supplierName}): {row.message}
                  </li>
                ))}
                {importSummary.patchFailuresCount > importSummary.patchFailures.length && <li>Additional items truncated...</li>}
              </ul>
            </div>
          )}
          <div className="import-split">
            <div className="import-pane csv-pane">
              <div className="pane-title">
                <strong>Source Columns</strong>
                <span>Drag columns into the supplier fields</span>
              </div>
              {importHeaders.length ? (
                <>
                  <div className="import-columns-grid">
                    {importHeaders.map((header, idx) => (
                      <div
                        key={`import-column-${idx}`}
                        className={[
                          'import-column-card',
                          mappedColumns.has(idx) ? 'mapped' : '',
                          draggingColumn === idx ? 'dragging' : '',
                        ].filter(Boolean).join(' ')}
                        draggable
                        onDragStart={(event) => handleColumnDragStart(idx, event)}
                        onDragEnd={handleColumnDragEnd}
                      >
                        <div className="column-card-title">
                          <span>{friendlyColumnLabel(idx)}</span>
                          {mappedColumns.has(idx) && <span className="column-badge">Mapped</span>}
                        </div>
                        <ul className="column-samples">
                          {importRows.slice(0, 3).map((row, sampleIdx) => (
                            <li key={`col-${idx}-sample-${sampleIdx}`}>
                              {row[idx] != null && String(row[idx]).trim() !== '' ? String(row[idx]) : <span className="muted">—</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                  <div className="import-preview-table">
                    <table>
                      <thead>
                        <tr>
                          {importHeaders.map((_, colIdx) => (
                            <th key={`preview-head-${colIdx}`}>{friendlyColumnLabel(colIdx)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {importRows.slice(0, 5).map((row, rowIdx) => (
                          <tr key={`preview-row-${rowIdx}`}>
                            {importHeaders.map((_, colIdx) => (
                              <td key={`preview-cell-${rowIdx}-${colIdx}`}>
                                {row[colIdx] != null && row[colIdx] !== '' ? String(row[colIdx]) : '—'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="import-empty-state">
                  Choose a CSV or Excel file to see the column preview.
                </div>
              )}
            </div>
            <div className="import-pane mapping-pane">
              <div className="pane-title">
                <strong>Supplier Fields</strong>
                <span>Drop a column or pick from the list</span>
              </div>
              <div className="import-fields-list">
                {supplierFieldDefinitions.map((field) => {
                  const mappedColumnIndex = importMapping[field.key];
                  const assigned = typeof mappedColumnIndex === 'number';
                  const fieldClasses = [
                    'import-field-card',
                    field.required ? 'required' : '',
                    dragOverField === field.key ? 'drag-over' : '',
                    assigned ? 'assigned' : '',
                  ].filter(Boolean).join(' ');
                  return (
                    <div
                      key={field.key}
                      className={fieldClasses}
                      onDragOver={(event) => handleFieldDragOver(field.key, event)}
                      onDragLeave={() => handleFieldDragLeave(field.key)}
                      onDrop={(event) => handleFieldDrop(field.key, event)}
                    >
                      <div className="field-label">
                        <strong>{field.label}</strong>
                        {field.required && <span className="required-indicator" title="Required">*</span>}
                      </div>
                      {field.description && <p className="field-description">{field.description}</p>}
                      <div className="field-assignment">
                        {assigned ? (
                          <div className="assigned-chip">
                            <span>{friendlyColumnLabel(mappedColumnIndex)}</span>
                            <button type="button" onClick={() => assignColumnToField(field.key, null)}>
                              Remove
                            </button>
                          </div>
                        ) : (
                          <span className="unmapped-hint">Drop a column here</span>
                        )}
                        <select
                          value={assigned ? String(mappedColumnIndex) : ''}
                          onChange={(event) => {
                            const value = event.target.value;
                            assignColumnToField(field.key, value === '' ? null : Number(value));
                          }}
                          disabled={!importHeaders.length}
                        >
                          <option value="">{importHeaders.length ? 'Select column...' : 'Upload a file first'}</option>
                          {importHeaders.map((_, idx) => (
                            <option key={`field-select-${field.key}-${idx}`} value={idx}>
                              {friendlyColumnLabel(idx)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>
              {previewPayloads.length > 0 && (
                <div className="mapped-preview">
                  <div className="pane-title">
                    <strong>Mapped Preview</strong>
                    <span>First {previewPayloads.length} row{previewPayloads.length === 1 ? '' : 's'}</span>
                  </div>
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Code</th>
                        <th>Email</th>
                        <th>Phone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewPayloads.map((row, idx) => (
                        <tr key={`mapped-preview-row-${idx}`}>
                          <td>{row.name || '—'}</td>
                          <td>{row.supplier_code || '—'}</td>
                          <td>{row.email || '—'}</td>
                          <td>{row.phone || row.mobile || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          <div className="import-footer">
            <div className="import-status">
              {importing ? (
                <span>Importing {importProgress.processed} / {importProgress.total}...</span>
              ) : readyRowCount ? (
                <span>{readyRowCount} row{readyRowCount === 1 ? '' : 's'} ready</span>
              ) : (
                <span>Waiting for a mapped file...</span>
              )}
              {!requiredMappingsSatisfied && missingRequiredFields.length > 0 && (
                <span className="missing-fields">
                  Required: {missingRequiredFields.map(fieldLabelFor).join(', ')}
                </span>
              )}
            </div>
            <div className="import-actions">
              <button className="btn" type="button" onClick={handleResetImport} disabled={importing}>Clear</button>
              <button
                className="btn primary"
                type="button"
                disabled={importing || !readyRowCount || !requiredMappingsSatisfied}
                onClick={handleRunImport}
              >
                {importing ? 'Importing...' : (readyRowCount ? `Import ${readyRowCount} Supplier${readyRowCount === 1 ? '' : 's'}` : 'Import Suppliers')}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && <div className="suppliers-error">{error}</div>}

      <div className="suppliers-table-card">
        {loading ? (
          <div className="loading">Loading suppliers…</div>
        ) : suppliers.length ? (
          <table className="suppliers-table">
            <thead>
              <tr>
                <th className="select-col" data-colkey="select">
                  <input
                    type="checkbox"
                    ref={selectAllCheckboxRef}
                    checked={suppliers.length > 0 && allVisibleSelected}
                    onChange={handleToggleSelectAllOnPage}
                    disabled={!suppliers.length}
                    aria-label="Select all suppliers on this page"
                  />
                </th>
                <th className="status-col" data-colkey="status">Active</th>
                <th className="actions-col" data-colkey="actions">Actions</th>
                <th data-colkey="name">Name</th>
                <th data-colkey="contact">Contact</th>
                <th data-colkey="email">Email</th>
                <th data-colkey="phone">Phone</th>
                <th data-colkey="terms">Payment Terms</th>
                <th data-colkey="leadtime">Lead Time (days)</th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map(s => (
                <tr
                  key={s.id}
                  className={(selectedSupplierId === s.id || selectedSupplierIds.has(s.id)) ? 'active' : ''}
                  onClick={() => setSelectedSupplierId(s.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="select-cell" data-colkey="select">
                    <input
                      type="checkbox"
                      checked={selectedSupplierIds.has(s.id)}
                      onChange={(event) => {
                        event.stopPropagation();
                        toggleSelectSupplier(s.id);
                      }}
                      onClick={(event) => event.stopPropagation()}
                      aria-label={`Select ${s.name || 'supplier'}`}
                    />
                  </td>
                  <td className="status-cell" data-colkey="status">
                    <span className={`status-pill ${s.active ? 'active' : 'inactive'}`}>
                      {s.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="actions-cell td-actions" data-colkey="actions">
                    <div className="actions">
                      <button
                        className="mini-btn"
                        title="Edit"
                        onClick={(e) => { e.stopPropagation(); openEdit(s); }}
                      >
                        <FaEdit size={14} />
                      </button>
                      <button
                        className="mini-btn danger"
                        title="Delete"
                        onClick={(e) => { e.stopPropagation(); requestDelete(s); }}
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>
                  </td>
                  <td data-colkey="name">
                    <div className="supplier-name">
                      <span className="supplier-label">{s.name}</span>
                      {s.supplier_code && <span className="supplier-code">{s.supplier_code}</span>}
                    </div>
                    {s.website && (
                      <a href={s.website} target="_blank" rel="noreferrer">
                        {s.website}
                      </a>
                    )}
                  </td>
                  <td data-colkey="contact">
                    <div>{s.contact_name || '—'}</div>
                    {s.contact_title && <div className="supplier-muted">{s.contact_title}</div>}
                  </td>
                  <td data-colkey="email">{s.email || '—'}</td>
                  <td data-colkey="phone">{s.phone || s.mobile || '—'}</td>
                  <td data-colkey="terms">{s.payment_terms || '—'}</td>
                  <td data-colkey="leadtime">{s.lead_time_days != null ? s.lead_time_days : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">No suppliers found.</div>
        )}
      </div>

      <div className="suppliers-pagination">
        <button className="btn" disabled={currentPage <= 1} onClick={() => changePage(-1)}>Prev</button>
        <span>Page {currentPage} of {totalPages}</span>
        <button className="btn" disabled={currentPage >= totalPages} onClick={() => changePage(1)}>Next</button>
        <span className="suppliers-total">{meta?.total || 0} total</span>
      </div>

      {formOpen && (
        <div className="modal-backdrop suppliers-modal-backdrop" onClick={closeForm}>
          <div className="modal suppliers-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editing ? `Edit ${editing.name}` : 'New Supplier'}</h3>
              <button className="icon-btn" onClick={closeForm} aria-label="Close">
                <FaTimes />
              </button>
            </div>
            <form className="suppliers-form" onSubmit={submitForm}>
              <div className="modal-body">
                {formError && <div className="suppliers-error">{formError}</div>}
                <div className="suppliers-form-sections">
                  <section className="suppliers-form-section">
                    <div className="suppliers-section-header">
                      <div className="suppliers-section-kicker">Supplier Setup</div>
                      <div className="suppliers-section-title">Core Details</div>
                    </div>
                    <div className="form-grid">
                      <label>
                        <span>Supplier Name *</span>
                        <input name="name" value={form.name} onChange={handleFormChange} required />
                      </label>
                      <label>
                        <span>Supplier Code</span>
                        <input name="supplier_code" value={form.supplier_code} onChange={handleFormChange} />
                      </label>
                      <label>
                        <span>Website</span>
                        <input name="website" value={form.website} onChange={handleFormChange} />
                      </label>
                      <label className="span-2 checkbox-field">
                        <input
                          type="checkbox"
                          name="active"
                          checked={form.active}
                          onChange={handleFormChange}
                        />
                        Supplier is active
                      </label>
                    </div>
                  </section>

                  <section className="suppliers-form-section">
                    <div className="suppliers-section-header">
                      <div className="suppliers-section-kicker">Contacts</div>
                      <div className="suppliers-section-title">People & Communication</div>
                    </div>
                    <div className="form-grid">
                      <label>
                        <span>Contact Name</span>
                        <input name="contact_name" value={form.contact_name} onChange={handleFormChange} />
                      </label>
                      <label>
                        <span>Contact Title</span>
                        <input name="contact_title" value={form.contact_title} onChange={handleFormChange} />
                      </label>
                      <label>
                        <span>Email</span>
                        <input type="email" name="email" value={form.email} onChange={handleFormChange} />
                      </label>
                      <label>
                        <span>Phone</span>
                        <input name="phone" value={form.phone} onChange={handleFormChange} />
                      </label>
                      <label>
                        <span>Mobile</span>
                        <input name="mobile" value={form.mobile} onChange={handleFormChange} />
                      </label>
                    </div>
                  </section>

                  <section className="suppliers-form-section">
                    <div className="suppliers-section-header">
                      <div className="suppliers-section-kicker">Location</div>
                      <div className="suppliers-section-title">Address</div>
                    </div>
                    <div className="form-grid">
                      <label className="span-2">
                        <span>Address Line 1</span>
                        <input name="address1" value={form.address1} onChange={handleFormChange} />
                      </label>
                      <label className="span-2">
                        <span>Address Line 2</span>
                        <input name="address2" value={form.address2} onChange={handleFormChange} />
                      </label>
                      <label>
                        <span>City</span>
                        <input name="city" value={form.city} onChange={handleFormChange} />
                      </label>
                      <label>
                        <span>State/Province</span>
                        <input name="state" value={form.state} onChange={handleFormChange} />
                      </label>
                      <label>
                        <span>Postal Code</span>
                        <input name="postal_code" value={form.postal_code} onChange={handleFormChange} />
                      </label>
                      <label>
                        <span>Country</span>
                        <input name="country" value={form.country} onChange={handleFormChange} />
                      </label>
                    </div>
                  </section>

                  <section className="suppliers-form-section">
                    <div className="suppliers-section-header">
                      <div className="suppliers-section-kicker">Purchasing</div>
                      <div className="suppliers-section-title">Terms & Defaults</div>
                    </div>
                    <div className="form-grid">
                      <label>
                        <span>Payment Terms</span>
                        <input name="payment_terms" value={form.payment_terms} onChange={handleFormChange} />
                      </label>
                      <label>
                        <span>Lead Time (days)</span>
                        <input
                          type="number"
                          min="0"
                          name="lead_time_days"
                          value={form.lead_time_days}
                          onChange={handleFormChange}
                        />
                      </label>
                      <label>
                        <span>Default Currency</span>
                        <select name="default_currency" value={form.default_currency} onChange={handleFormChange}>
                          {currencyOptions.map(cur => (
                            <option key={cur} value={cur}>{cur}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        <span>Min Order Amount</span>
                        <input
                          type="number"
                          step="0.01"
                          name="min_order_amount"
                          value={form.min_order_amount}
                          onChange={handleFormChange}
                        />
                      </label>
                      <label>
                        <span>Tax ID / VAT</span>
                        <input name="tax_id" value={form.tax_id} onChange={handleFormChange} />
                      </label>
                      <label>
                        <span>Preferred Shipping Method</span>
                        <input name="preferred_shipping_method" value={form.preferred_shipping_method} onChange={handleFormChange} />
                      </label>
                      <label className="span-2">
                        <span>Notes</span>
                        <textarea name="notes" rows={3} value={form.notes} onChange={handleFormChange} />
                      </label>
                    </div>
                  </section>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn" type="button" onClick={closeForm} disabled={saving}>Cancel</button>
                <button className="btn primary" type="submit" disabled={saving}>
                  {saving ? 'Saving…' : (editing ? 'Save Changes' : 'Create Supplier')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="modal-backdrop suppliers-modal-backdrop" onClick={cancelDelete}>
          <div className="modal suppliers-modal small" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Supplier</h3>
              <button className="icon-btn" onClick={cancelDelete} aria-label="Close">
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This will hide the supplier from the list.
            </div>
            <div className="modal-footer">
              <button className="btn" disabled={deleting} onClick={cancelDelete}>Cancel</button>
              <button className="btn danger" disabled={deleting} onClick={confirmDelete}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkDeleteOpen && (
        <div className="modal-backdrop suppliers-modal-backdrop" onClick={closeBulkDeleteModal}>
          <div className="modal suppliers-modal small" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Selected Suppliers</h3>
              <button className="icon-btn" onClick={closeBulkDeleteModal} aria-label="Close" disabled={bulkDeleting}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              Are you sure you want to delete {selectedCount} supplier{selectedCount === 1 ? '' : 's'}? This will hide them from the list.
            </div>
            <div className="modal-footer">
              <button className="btn" disabled={bulkDeleting} onClick={closeBulkDeleteModal}>Cancel</button>
              <button className="btn danger" disabled={bulkDeleting} onClick={runBulkDelete}>
                {bulkDeleting ? 'Deleting...' : `Delete ${selectedCount}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Suppliers;
